import { Box, HStack, Checkbox, Skeleton } from '@chakra-ui/react';
import { ColumnDefResolved, Row } from '@tanstack/react-table';
import { Avatar, IconButton, Tag, TruncatableText } from 'Atoms';
import { NestedTable, TableData } from 'Molecules/NestedTable';
import { Typography } from 'Tokens';
import { ArrowNarrowRightIcon } from 'Tokens/Icons/Direction';
import { AIIcon } from 'Tokens/Icons/Function';
import {
  MetricsTableData,
  areArraysOfObjectsEqual,
  areChildrenMaterialOrCollected,
  isFullMetricQuarterlyLevel,
} from 'containers/Esrs';
import { mapUnitToCompanyCurrency } from 'containers/Esrs/utils';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FrequencyEnums, TimePeriods, TimePeriodsEnums } from '../../Requirement';
import {
  AggregatedMetricsTableData,
  AggregatedQualitativeAnswers,
  hasChildOnBULevel,
  hasChildOnSubLevel,
  hasQuarterlyFrequency,
  isFullMetricOnBULevel,
  isFullParentMetricOnSubLevel,
} from '../AggregatedMetrics/AggregatedMetrics.hooks';
import { isEqual } from 'lodash';
import {
  GetAssessmentCurrencyConversionQuery_,
  GetRequiredMaterialMetricsQuery_,
  QuestionType_Enum_,
  TaskStatusEnum_Enum_,
} from 'models';
import { GeneratedAnswer } from '../MetricAI';
import { MetricOwnerAvatar } from '../InputTable/MetricOwnerSelect';
import { CommentsCounter } from '../AggregatedMetrics/AggregatedMetricsUtils';
import { MetricsAttachmentsLabel } from '../InputTable/MetricAttachmentsLabel';
import { NarrativeMetricsInput } from './MetricsTableComponents/InputFields/NarrativeInputs/NarrativeInputs';
import { QuantitativeMetricsInput } from './MetricsTableComponents/InputFields/QuantitativeInputs/QuantitativeInputs';
import { LastEditedBy } from './MetricsTableComponents/LastEdited/LastEdited';
import { getLastEditedData } from './MetricsTableComponents/LastEdited/LastEdited.hooks';
import { MetricProgress } from './MetricsTableComponents/MetricProgress/MetricProgress';
import { MetricAIAnswer } from './MetricsTableComponents/MetricYear/MetricYear.tsx';
import { flattenNarrativeMetricRows } from '../Metrics.utils';
import { AnswerApprovalCell } from './MetricsTable.utils';
import { RemoveAdditionalInfo } from './MetricsTableComponents/AdditionalInfoModals/AdditionalInfoModals';
import {
  AISelectedRows,
  SelectedRowData,
  SelectedRows,
} from './MetricsTableComponents/SelectedRows/SelectedRows';
import { DataPoint } from './MetricsTableComponents/DataPoint/DataPoint';
import { Reference } from './MetricsTableComponents/Reference/Reference.tsx';
import { Unit } from './MetricsTableComponents/Unit/Unit.tsx';
import {
  AssessableMetrics,
  GeneratedSummaryStatus,
} from '../Metrics/ProcessMetrics/MetricsHelperFunctions.ts';
import { TASK_STATUS_TAG } from '../Metrics/MetricsComponents/MetricTableHeader.tsx';

export const MetricsTable = ({
  metrics,
  selectedQuarter,
  isGroup,
  currency,
  materialStandardId,
  parentStandardId,
  companyReportingUnit,
  rowData,
  setRowData,
  pageSize = 5,
  allMetrics,
  setNumberOfRows,
  isMetricDr,
  tasksLoading,
  selectedNumericMetrics,
  setSelectedNumericMetrics,
  selectedNarrativeMetrics,
  setSelectedNarrativeMetrics,
  isAI = false,
  answersData,
  setIsComments,
  generatedAnswers,
  setSelectedRow,
  onApprovalWarningModalOpen,
  isGeneratingAnswers,
  setGeneratedAnswers,
  populateQuantitativeAnswers,
  populateNarrativeAnswers,
  setSelectedRows,
  selectedRows,
  checkboxesDisabled = true,
  generatedSummaryStatus,
  isNarrative,
  isReadOnly,
  isBusinessUnit,
  requiredMaterialMetrics,
  notAggregatableMetrics,
  metricsMissingTagsConfiguration,
  parentNotAggregatableMetrics,
  optedOutMetrics,
  currencyConversionData,
  metricStatus,
  isTableHovered,
}: {
  metrics: AggregatedMetricsTableData[];
  selectedQuarter: TimePeriods;
  isGroup: boolean;
  currency: string;
  answersData?: AggregatedQualitativeAnswers;
  materialStandardId: string;
  parentStandardId: string | undefined;
  companyReportingUnit?: string;
  setSelectedRows: Dispatch<SetStateAction<SelectedRowData[]>>;
  selectedRows: SelectedRowData[];
  onApprovalWarningModalOpen?: () => void;
  isGeneratingAnswers?: boolean;
  rowData?: MetricsTableData;
  setSelectedRow?: React.Dispatch<React.SetStateAction<string>>;
  setRowData: (
    param:
      | (MetricsTableData & {
          sourceData?: AggregatedQualitativeAnswers[number];
        })
      | undefined
  ) => void;
  pageSize?: number;
  allMetrics?: MetricsTableData[];
  setNumberOfRows?: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  isMetricDr: boolean;
  selectedNumericMetrics?: MetricsTableData[];
  setSelectedNumericMetrics?: Dispatch<SetStateAction<MetricsTableData[]>>;
  selectedNarrativeMetrics?: AssessableMetrics;
  setSelectedNarrativeMetrics?: Dispatch<SetStateAction<AssessableMetrics>>;
  isAI?: boolean;
  setIsComments: (val: boolean) => void;
  generatedAnswers?: GeneratedAnswer[];
  tasksLoading: boolean;
  setGeneratedAnswers?: Dispatch<SetStateAction<GeneratedAnswer[]>>;
  populateQuantitativeAnswers?: (
    generatedAnswers: GeneratedAnswer[],
    numericMetrics: MetricsTableData[]
  ) => Promise<void>;
  generatedSummaryStatus?: GeneratedSummaryStatus;
  populateNarrativeAnswers?: (
    generatedAnswers: GeneratedAnswer[],
    numericMetrics: AssessableMetrics
  ) => Promise<void>;
  checkboxesDisabled?: boolean;
  isNarrative?: boolean;
  isReadOnly?: boolean;
  isBusinessUnit?: boolean;
  metricStatus: TaskStatusEnum_Enum_ | undefined;
  requiredMaterialMetrics: GetRequiredMaterialMetricsQuery_['esrs_MaterialMetric'];
  notAggregatableMetrics?: string[];
  metricsMissingTagsConfiguration?: string[];
  parentNotAggregatableMetrics?: Set<string>;
  optedOutMetrics?: string[];
  currencyConversionData?: GetAssessmentCurrencyConversionQuery_['esrs_AssessmentCurrencyConversion'];
  isTableHovered?: boolean;
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlDatapointId = useMemo(() => searchParams.get('datapointId'), [searchParams]);
  const isMdrmSidebarOpen = useMemo(() => !!searchParams.get('openMdrm'), [searchParams]);
  const selectedDataPoint = useMemo(() => {
    return { tagName: rowData?.tagName, reference: rowData?.sourceData?.metricRef };
  }, [rowData]);

  const openDrawer = new URLSearchParams(location.search).get('openDrawer');

  const isMixed = useMemo(
    () => hasChildOnBULevel(metrics[0].metric) && hasChildOnSubLevel(metrics[0].metric),
    [metrics]
  );

  const showProgress = useMemo(() => {
    if (isNarrative) return false;

    if (isBusinessUnit) return hasQuarterlyFrequency(metrics[0].metric);
    return (
      hasQuarterlyFrequency(metrics[0].metric) ||
      (hasChildOnSubLevel(metrics[0].metric) && isGroup) ||
      (hasChildOnBULevel(metrics[0].metric) && !isBusinessUnit)
    );
  }, [metrics, isNarrative, isBusinessUnit, isGroup]);

  const progressLabel = useMemo(() => {
    return hasChildOnSubLevel(metrics[0].metric) && isGroup
      ? 'Subsidiaries'
      : hasChildOnBULevel(metrics[0].metric) && !isBusinessUnit
        ? 'Business units'
        : 'Quarters';
  }, [metrics, isGroup]);

  const progressTooltip = useMemo(() => {
    if (hasChildOnBULevel(metrics[0].metric) && isGroup) {
      return 'This data point is collected from subsidiaries. The progress indicates whether all subsidiaries have added their answers.';
    } else if (hasChildOnBULevel(metrics[0].metric)) {
      return 'This data point is collected from business units. The progress indicates whether all business units have added their answers.';
    }
    return 'This data point is collected quarterly. The progress indicates whether data is collected from all quarters or only a few.';
  }, [metrics]);

  const handleRowClick = (row: MetricsTableData, isComments: boolean) => {
    if (isMdrmSidebarOpen) {
      searchParams.delete('openMdrm');
      setSearchParams(searchParams);
    }
    if (!row.referenceToSource) {
      setSelectedRow?.(row.metric.reference);
      if (openDrawer && !!urlDatapointId) navigate(pathname.split('?')[0]);
      if (generatedSummaryStatus?.isApproved === false) {
        if (onApprovalWarningModalOpen) onApprovalWarningModalOpen();
      } else {
        if (isEqual(rowData, row)) {
          setRowData(undefined);
        } else {
          setRowData({
            ...row,
            sourceData: answersData?.find((ans) => ans.metricRef === row.metric.reference),
          });
          if (isComments) {
            setIsComments(true);
          } else setIsComments(false);
        }
      }
    }
  };
  const flattenedMetrics = useMemo(() => flattenNarrativeMetricRows(metrics), [metrics]);

  const allMetricsChecked = useMemo(
    () =>
      flattenedMetrics.every((metric) =>
        selectedNarrativeMetrics?.some((row) => row.reference === metric.reference)
      ),
    [selectedNarrativeMetrics, flattenedMetrics]
  );

  const someMetricsChecked = useMemo(
    () =>
      !allMetricsChecked &&
      flattenedMetrics.some((metric) =>
        selectedNarrativeMetrics?.some((row) => row.reference === metric.reference)
      ),
    [selectedNarrativeMetrics, flattenedMetrics, allMetricsChecked]
  );

  const handleCheckAll = useCallback(() => {
    if (allMetricsChecked) {
      setSelectedNarrativeMetrics?.([]);
    } else {
      setSelectedNarrativeMetrics?.(flattenedMetrics);
    }
  }, [allMetricsChecked, flattenedMetrics, setSelectedNarrativeMetrics]);

  const isNotSpecialRow = (row: Row<TableData<MetricsTableData>>) => {
    return (
      !row.original.referenceToSource &&
      !row.original.locked &&
      !row?.original.isAdditionalInfoButton &&
      !row.original.isAdditionalInfo
    );
  };

  const showRowProgress = (row: Row<TableData<MetricsTableData>>) => {
    const metricRow = row.original;
    return (
      metricRow &&
      metricRow.metric.calculation !== 'NONE' &&
      isNotSpecialRow(row) &&
      (hasQuarterlyFrequency(metricRow.metric) ||
        (hasChildOnBULevel(metricRow.metric) && !isBusinessUnit) ||
        (hasChildOnSubLevel(metricRow.metric) && isGroup))
    );
  };

  const showCheckbox = (row: AggregatedMetricsTableData) => {
    const frequency = row.metric.materialMetrics?.[0]?.frequency;
    const isCollectedQuarterly = isFullMetricQuarterlyLevel(row.metric);
    if (row.isAdditionalInfo || row.isAdditionalInfoButton || row.referenceToSource || row.locked)
      return false;
    if (isCollectedQuarterly && selectedQuarter !== 'Year') return true;
    if (
      (frequency === FrequencyEnums.yearly && selectedQuarter !== 'Year') ||
      (isCollectedQuarterly && selectedQuarter === 'Year')
    )
      return false;
    return true;
  };

  const checkboxPropsRef = useRef({ isTableHovered, selectedRows });
  useEffect(() => {
    checkboxPropsRef.current = { isTableHovered, selectedRows };
  });

  const nestedColumns: ColumnDefResolved<TableData<AggregatedMetricsTableData>, any>[] | undefined =
    useMemo(
      () => [
        {
          header: '',
          id: 'AICheckbox',
          meta: {
            width: '32px',
            padding: '16px 8px',
            verticalAlign: 'top',
            headerComponent: isAI && isNarrative && (
              <Checkbox
                isDisabled={checkboxesDisabled}
                isChecked={allMetricsChecked}
                isIndeterminate={someMetricsChecked}
                onChange={handleCheckAll}
              />
            ),
          },
          accessorKey: 'selectedRows',
          cell: ({ row }) => (
            <AISelectedRows
              isNarrative={true}
              row={row.original}
              checkboxesDisabled={checkboxesDisabled}
              selectedNarrativeMetrics={selectedNarrativeMetrics}
              selectedNumericMetrics={selectedNumericMetrics}
              setSelectedNarrativeMetrics={setSelectedNarrativeMetrics}
              setSelectedNumericMetrics={setSelectedNumericMetrics}
            />
          ),
        },
        {
          header: '',
          id: 'Checkbox',
          meta: {
            width: '14px',
            padding: isNarrative ? '16px 4px' : '14px 4px',
            verticalAlign: 'top',
            isTableHovered,
          },
          accessorKey: 'Checkbox',
          cell: ({ row }) => {
            const props = checkboxPropsRef?.current;
            return !!(props.isTableHovered || props.selectedRows?.length) &&
              showCheckbox(row.original) ? (
              <SelectedRows
                row={row}
                selectedRows={props.selectedRows}
                selectedQuarter={selectedQuarter}
                setSelectedRows={setSelectedRows}
              />
            ) : (
              <Box minW="16px"></Box>
            );
          },
        },
        {
          header: 'Data point',
          meta: {
            width: '40%',
            verticalAlign: isNarrative ? 'top' : 'center',
            padding: isNarrative ? '0px 8px' : undefined,
          },
          accessorKey: 'metric',
          cell: ({ row }) => (
            <DataPoint
              row={row}
              notAggregatableMetrics={notAggregatableMetrics}
              parentNotAggregatableMetrics={parentNotAggregatableMetrics}
              metricsMissingTagsConfiguration={metricsMissingTagsConfiguration}
              isNarrative={isNarrative}
              setNumberOfRows={setNumberOfRows}
              allMetrics={allMetrics}
              handleRowClick={handleRowClick}
              companyReportingUnit={companyReportingUnit}
              optedOutMetrics={optedOutMetrics}
              isGroup={isGroup}
            />
          ),
        },
        {
          header: 'Ref.',
          meta: {
            width: '88px',
            verticalAlign: isNarrative ? 'top' : 'center',
            padding: '14px 8px',
          },
          accessorKey: 'reference',
          cell: ({ row }) => (
            <Reference
              row={row}
              isNotSpecialRow={isNotSpecialRow}
              optedOutMetrics={optedOutMetrics}
              isNarrative={isNarrative}
            />
          ),
        },
        {
          header: isNarrative
            ? 'Answer'
            : selectedQuarter !== TimePeriodsEnums.year
              ? selectedQuarter
              : "Year's total",

          meta: {
            width: (() => {
              if (!showProgress && !isMetricDr) return '38%';
              if (showProgress) return '20%';
              return isNarrative ? '41%' : '30%';
            })(),
            verticalAlign: isNarrative ? 'top' : 'center',
            padding: isNarrative ? '6px 8px' : undefined,
          },
          accessorKey: 'yearly',
          cell: ({ row }) => {
            const { metric } = row.original;

            const isMetricMaterialForCompany =
              metric.materialMetrics?.[0]?.isMaterial === true &&
              metric.materialMetrics?.[0]?.materialStandard?.isMaterial === true;

            const isMetricMaterialForGroup = (
              metric: TableData<AggregatedMetricsTableData>['metric'],
              parentStandardId: string,
              requiredMaterialMetrics: GetRequiredMaterialMetricsQuery_['esrs_MaterialMetric']
            ): boolean => {
              if (!!metric?.childrenMetrics?.length) {
                return metric.childrenMetrics?.every(
                  (child) =>
                    child.childMetric &&
                    isFullParentMetricOnSubLevel(
                      child.childMetric,
                      parentStandardId,
                      requiredMaterialMetrics
                    )
                );
              }
              return !!requiredMaterialMetrics?.find(
                (mm) =>
                  mm.materialStandardId === parentStandardId &&
                  mm.metric.reference === metric.reference
              );
            };

            const showMetricProgress =
              hasChildOnBULevel(metric) ||
              (hasChildOnSubLevel(metric) && isGroup) ||
              hasQuarterlyFrequency(metric);

            const isCollectedOnCompanyLevel =
              !isFullMetricOnBULevel(metric) &&
              isBusinessUnit &&
              (isFullParentMetricOnSubLevel(
                metric,
                parentStandardId ?? '',
                requiredMaterialMetrics
              ) ||
                isMetricMaterialForCompany);

            const isCollectedOnGroupLevel =
              !isFullMetricOnBULevel(metric) &&
              !!parentStandardId &&
              !isGroup &&
              !areChildrenMaterialOrCollected(metric, parentStandardId, requiredMaterialMetrics) &&
              !isMetricMaterialForCompany &&
              isMetricMaterialForGroup(metric, parentStandardId, requiredMaterialMetrics);

            if (row.original.isAdditionalInfoButton) return null;

            const { locked, referenceToSource, isAdditionalInfo, additionalInfoPlacement } =
              row.original;

            if (
              isCollectedOnCompanyLevel &&
              metric.metricType !== QuestionType_Enum_.YesNo_ &&
              metric.metricType !== QuestionType_Enum_.MultipleChoice_
            ) {
              return (
                <Typography variant="body" w="100%" color="text.hint">
                  Collected on company level
                </Typography>
              );
            }

            if (
              isCollectedOnGroupLevel &&
              metric.metricType !== QuestionType_Enum_.YesNo_ &&
              metric.metricType !== QuestionType_Enum_.MultipleChoice_
            )
              return (
                <Typography variant="body" w="100%" color="text.hint">
                  Collected on group level
                </Typography>
              );

            if (metric.metricType !== QuestionType_Enum_.Decimal_ || isAdditionalInfo) {
              if (!locked) {
                return (
                  <NarrativeMetricsInput
                    metric={metric}
                    companyReportingUnit={companyReportingUnit ?? ''}
                    isAI={isAI}
                    isAdditionalInfo={isAdditionalInfo}
                    additionalInfoPlacement={additionalInfoPlacement}
                    isReadOnly={isReadOnly ?? false}
                    isGeneratingAnswers={isGeneratingAnswers}
                    setRowData={setRowData}
                    answersData={answersData}
                    generatedAnswers={generatedAnswers}
                    isCollectedOnGroupLevel={isCollectedOnGroupLevel}
                  />
                );
              }
            } else {
              if (!referenceToSource && !locked) {
                return (
                  <HStack justifyContent="start" alignItems="center">
                    <Box w={isNarrative ? '50%' : '100%'}>
                      <QuantitativeMetricsInput
                        row={row}
                        metricRow={row.original}
                        metrics={metrics}
                        allMetrics={allMetrics}
                        isNarrativeTable={isNarrative}
                        companyReportingUnit={companyReportingUnit ?? ''}
                        isGroup={isGroup}
                        isBusinessUnit={isBusinessUnit}
                        setRowData={setRowData}
                        selectedQuarter={selectedQuarter}
                      />
                    </Box>
                    {isNarrative && (
                      <HStack alignItems="center" justifyContent="space-between" w="50%">
                        <TruncatableText
                          w="100%"
                          pl={'35px'}
                          text={mapUnitToCompanyCurrency(
                            metric.unitOfMeasurement ?? 'NA',
                            currency
                          )}
                        />
                        {showMetricProgress && (
                          <Box minW="84px" p="6px 8px">
                            <MetricProgress
                              row={row.original}
                              answersData={answersData}
                              isGroup={isGroup}
                              companyReportingUnit={companyReportingUnit}
                            />
                          </Box>
                        )}
                      </HStack>
                    )}
                  </HStack>
                );
              }
            }
          },
        },
        {
          header: 'Unit',
          meta: {
            width: '88px',
          },
          accessorKey: 'unit',
          cell: ({ row }) => (
            <Unit
              row={row}
              materialStandardId={materialStandardId}
              currency={currency}
              optedOutMetrics={optedOutMetrics}
            />
          ),
        },
        {
          header: 'Answer',
          accessorKey: 'AIAnswer',
          meta: {
            padding: '14px 8px',
            verticalAlign: 'top',
            width: '350px',
          },
          cell: ({ row }) => {
            const generatedAnswer = generatedAnswers?.find(
              (a) =>
                a.metricRef === row.original.metric.reference &&
                areArraysOfObjectsEqual(a.tags, row.original.tags)
            );

            return (
              isNotSpecialRow(row) && (
                <MetricAIAnswer
                  row={row.original}
                  companyReportingUnit={companyReportingUnit}
                  nestedMetrics={metrics}
                  generatedAnswer={generatedAnswer}
                  isAI={isAI}
                  selectedQuarter={selectedQuarter}
                />
              )
            );
          },
        },
        {
          header: 'Progress',
          meta: {
            width: '84px',
            subtitle: isMixed ? undefined : progressLabel,
            verticalAlign: 'center',
            tooltip: isMixed ? undefined : progressTooltip,
          },
          accessorKey: 'progress',
          cell: ({ row }) => {
            return showRowProgress(row) ? (
              <MetricProgress
                row={row.original}
                answersData={answersData}
                isGroup={isGroup}
                companyReportingUnit={companyReportingUnit}
              />
            ) : (
              <></>
            );
          },
        },
        {
          header: 'Source',
          meta: {
            width: '10%',
            padding: '14px 8px',
            verticalAlign: 'top',
          },
          accessorKey: 'source',
          cell: ({ row }) => {
            return (
              <MetricsAttachmentsLabel
                row={row.original}
                companyReportingUnitId={companyReportingUnit}
              />
            );
          },
        },
        {
          header: 'Last edited',
          accessorKey: 'lastEdited',
          meta: {
            width: '10%',
            padding: '14px 8px',
            verticalAlign: 'top',
          },
          cell: ({ row }) => {
            const generatedAnswer = generatedAnswers?.find(
              (a) =>
                a.metricRef === row.original.metric.reference &&
                areArraysOfObjectsEqual(a.tags, row.original.tags)
            );
            const isAwaitingApproval =
              generatedAnswer?.answer &&
              generatedAnswer?.status === null &&
              generatedAnswer?.answer !== '-' &&
              generatedAnswer?.answer !== 'NA';

            return (
              !row.original.referenceToSource &&
              (isAwaitingApproval ? (
                <HStack spacing="4px">
                  <Avatar name="AI" size="xs" />
                  <Typography variant="body">{getLastEditedData(new Date()).date}</Typography>
                </HStack>
              ) : (
                <LastEditedBy
                  row={row.original}
                  selectedQuarter={selectedQuarter}
                  reportingUnitId={companyReportingUnit ? companyReportingUnit : undefined}
                />
              ))
            );
          },
        },
        {
          header: 'Owner',
          meta: {
            width: '54px',
            verticalAlign: isNarrative ? 'top' : 'center',
            padding: '14px 8px',
          },
          accessorKey: 'ownerId',
          cell: ({ row }) =>
            isNotSpecialRow(row) && (
              <MetricOwnerAvatar
                row={row.original}
                selectedQuarter={selectedQuarter}
                companyReportingUnit={companyReportingUnit}
              />
            ),
        },
        {
          // @ts-ignore
          header: isNarrative
            ? () => {
                return metricStatus ? (
                  <Skeleton isLoaded={!tasksLoading}>
                    <Tag
                      variant={TASK_STATUS_TAG[metricStatus]?.variant}
                      title={TASK_STATUS_TAG[metricStatus]?.title}
                      size="xs"
                    />
                  </Skeleton>
                ) : (
                  <></>
                );
              }
            : '',
          meta: {
            width: '6%',
            verticalAlign: isNarrative ? 'top' : 'center',
            padding: isNarrative ? '10px 8px' : '6px 8px',
          },
          accessorKey: 'actions',
          cell: ({ row }) => {
            return row.original.isAdditionalInfo ? (
              <HStack gap="2px" justifyContent="end">
                <RemoveAdditionalInfo
                  metric={row.original.metric}
                  companyReportingUnit={companyReportingUnit}
                  placement={row.original.additionalInfoPlacement ?? 'before'}
                />
              </HStack>
            ) : (
              row.original && isNotSpecialRow(row) && (
                <HStack gap="2px">
                  <CommentsCounter
                    row={row.original}
                    selectedQuarter={selectedQuarter}
                    companyReportingUnit={companyReportingUnit}
                    onClick={() => handleRowClick(row.original, true)}
                  />
                  <IconButton
                    className="metricArrow"
                    aria-label="side-bar"
                    size="sm"
                    variant="ghost"
                    icon={<ArrowNarrowRightIcon />}
                    onClick={() => handleRowClick(row.original, false)}
                  />
                </HStack>
              )
            );
          },
        },
        {
          // @ts-ignore
          header: () => {
            return (
              <HStack spacing="8px">
                <AIIcon />
                <Typography variant="bodyStrong">Approve</Typography>
              </HStack>
            );
          },
          meta: {
            padding: '10px 8px',
            verticalAlign: 'top',
            headerComponent: (
              <HStack spacing="8px">
                <AIIcon />
                <Typography variant="detailStrong">Approve</Typography>
              </HStack>
            ),
          },
          accessorKey: 'ai-approval',
          cell: ({ row }) => {
            const generatedAnswer = generatedAnswers?.find(
              (a) =>
                a.metricRef === row.original.metric.reference &&
                areArraysOfObjectsEqual(a.tags, row.original.tags)
            );

            const isGeneratedAnswerValid =
              generatedAnswer?.answer &&
              generatedAnswer.answer !== 'NA' &&
              generatedAnswer.answer !== '-';

            return row.original.metric.metricType !== QuestionType_Enum_.Decimal_
              ? generatedAnswer && generatedAnswer.answer !== '-' && (
                  <AnswerApprovalCell
                    generatedAnswers={generatedAnswers ?? []}
                    setGeneratedAnswers={setGeneratedAnswers}
                    row={{
                      metric: row.original.metric as MetricsTableData['metric'],
                      tags: [],
                    }}
                    companyReportingUnitId={companyReportingUnit}
                    populateNarrativeAnswers={populateNarrativeAnswers}
                  />
                )
              : !row.original.referenceToSource && isGeneratedAnswerValid && (
                  <AnswerApprovalCell
                    row={row.original}
                    populateQuantitativeAnswers={populateQuantitativeAnswers}
                    generatedAnswers={generatedAnswers ?? []}
                    setGeneratedAnswers={setGeneratedAnswers}
                    companyReportingUnitId={companyReportingUnit}
                  />
                );
          },
        },
      ],
      [
        selectedQuarter,
        materialStandardId,
        checkboxesDisabled,
        selectedNarrativeMetrics,
        selectedNumericMetrics,
        generatedAnswers,
        isMdrmSidebarOpen,
        searchParams,
        optedOutMetrics,
        metricStatus,
        tasksLoading,
      ]
    );

  const filteredColumns = useMemo(() => {
    if (!nestedColumns) return [];

    return nestedColumns.filter((col) => {
      const { accessorKey = '' } = col;

      const conditions = [
        !showProgress && accessorKey === 'progress',
        isNarrative && ['unit', 'progress', 'mdr-m'].includes(accessorKey),
        !(isAI && selectedNumericMetrics && setSelectedNumericMetrics) &&
          accessorKey === 'selectedRows',
        isAI && ['unit', 'progress', 'yearly', 'mdr-m'].includes(accessorKey),
        !isAI && ['AIAnswer', 'source', 'lastEdited'].includes(accessorKey),
        !(isAI && !!generatedAnswers?.length) && accessorKey === 'ai-approval',
        !isMetricDr && accessorKey === 'mdr-m',
      ];

      return !conditions.some(Boolean);
    });
  }, [
    nestedColumns,
    selectedQuarter,
    showProgress,
    isNarrative,
    isAI,
    selectedNumericMetrics,
    setSelectedNumericMetrics,
    generatedAnswers,
    isMetricDr,
  ]);

  return (
    <NestedTable<AggregatedMetricsTableData>
      columns={filteredColumns}
      data={metrics}
      expanded={true}
      pageSize={isNarrative ? undefined : pageSize}
      headerVariant="detailStrong"
      withInnerBorder={!isNarrative}
      withoutBorderTop={isNarrative}
      cellProps={(row) => {
        if (isNarrative) {
          return {
            borderTop:
              !row?.isAdditionalInfoButton &&
              !row?.isAdditionalInfo &&
              (!row?.metric.parentMetrics.length ||
                row?.metric.metricType === QuestionType_Enum_.Decimal_)
                ? '1px solid'
                : 'none',
            borderBottom:
              row?.metric.metricType === QuestionType_Enum_.Decimal_ ? '1px solid' : 'none',
            borderColor: 'border.decorative',
          };
        }
        return {};
      }}
      rowProps={(row) => {
        const generatedAnswer = generatedAnswers?.find(
          (a) => a.metricRef === row?.metric.reference && areArraysOfObjectsEqual(a.tags, row?.tags)
        )?.answer;

        const isRowSelected = isEqual(
          {
            metric: row?.metric,
            sourceData: answersData?.find((ans) => ans.metricRef === row?.metric.reference),
          },
          rowData
        );

        const getBackgroundColor = () => {
          if (row?.locked) return 'bg.disabled';

          if (row?.isAdditionalInfoButton || row?.referenceToSource || row?.isAdditionalInfo)
            return '';

          if (
            selectedDataPoint?.tagName === row?.tagName &&
            selectedDataPoint.reference === row?.metric.reference
          )
            return 'bg.pressed';

          if (isRowSelected) return 'bg.hover';
          if (generatedAnswer === 'NA' || generatedAnswer === '-') return 'bg.warning';
          if (generatedAnswer) return 'bg.selected';

          return '';
        };

        return {
          hidden: isAI && (row?.isAdditionalInfoButton || row?.isAdditionalInfo),
          bg: getBackgroundColor(),
          transition: '0.1s',
          sx: {
            '.metricTitle': isRowSelected
              ? {
                  textDecoration: 'underline',
                  textDecorationColor: 'text.hint',
                  textUnderlineOffset: '2px',
                }
              : {},
          },
          _hover:
            !row?.isAdditionalInfoButton && !row?.locked && !row?.isAdditionalInfo
              ? {
                  transition: '0.15s ease-in-out',
                  bg: 'bg.hover',
                  '.metricTitle': {
                    textDecoration: 'underline',
                    textDecorationColor: 'text.hint',
                    textUnderlineOffset: '2px',
                  },
                  '&:hover .metricArrow': {
                    bg: 'bg.hover',
                  },
                }
              : {},
        };
      }}
    />
  );
};
